import { useCallback, useEffect, useRef, useState } from "react"
import { getServerUrl, regionForLevel } from "../utils.js"
import { track } from "../analytics.js"

function formatToolCall(action, input, result) {
  const params = Object.entries(input || {})
    .map(([k, v]) => `${k}=${JSON.stringify(v)}`)
    .join(", ")
  return `${action}(${params}) = ${result}`
}

// status: 'IDLE' | 'PENDING' | 'STREAMING' | 'DONE' | 'ERROR'
//   PENDING   = run() called, EventSource open, no event yet
//   STREAMING = first event landed
//   DONE      = SSE 'done' fired
//   ERROR     = SSE 'error' or connection lost
export function useSolver() {
  const [status, setStatus] = useState("IDLE")
  const [problem, setProblem] = useState(null)
  const [level, setLevel] = useState(null)
  const [steps, setSteps] = useState([])
  const [answer, setAnswer] = useState(null)
  const [error, setError] = useState(null)
  const unsubRef = useRef(null)

  const stop = useCallback(() => {
    if (unsubRef.current) {
      unsubRef.current()
      unsubRef.current = null
    }
  }, [])

  const reset = useCallback(() => {
    stop()
    setStatus("IDLE")
    setProblem(null)
    setLevel(null)
    setSteps([])
    setAnswer(null)
    setError(null)
  }, [stop])

  const loadCached = useCallback(
    ({ problem: nextProblem, level: nextLevel, steps: nextSteps, answer: nextAnswer }) => {
      stop()
      console.log("[EqSolve solver] cache hit", {
        problem: nextProblem,
        level: nextLevel,
        steps: Array.isArray(nextSteps) ? nextSteps.length : 0,
      })
      setProblem(nextProblem)
      setLevel(nextLevel)
      setSteps(Array.isArray(nextSteps) ? nextSteps : [])
      setAnswer(nextAnswer || null)
      setError(null)
      setStatus("DONE")
    },
    [stop]
  )

  const run = useCallback(
    (nextProblem, nextLevel, source) => {
      stop()
      setProblem(nextProblem)
      setLevel(nextLevel)
      setSteps([])
      setAnswer(null)
      setError(null)
      setStatus("PENDING")

      const region = regionForLevel(nextLevel)
      const collected = new Map()
      let answerData = null
      let failTracked = false
      const trackFail = (kind, message) => {
        if (failTracked) return
        failTracked = true
        track("solve_failed", { problem: nextProblem, level: nextLevel, region, source, error_kind: kind, message })
      }

      const markStreaming = () => setStatus((prev) => (prev === "PENDING" ? "STREAMING" : prev))
      const encoded = encodeURIComponent(nextProblem)
      const url = getServerUrl(`api/solve-stream?problem=${encoded}&level=${nextLevel}`)
      const startedAt = performance.now()
      console.log("[EqSolve solver] request", { problem: nextProblem, level: nextLevel, url })
      const stream = new EventSource(url)
      let errored = false
      let timeout = null

      const resetTimeout = () => {
        clearTimeout(timeout)
        timeout = setTimeout(() => {
          stream.close()
          console.warn("[EqSolve solver] request timed out", { problem: nextProblem, level: nextLevel })
          setError({ kind: "OFFLINE", message: "Request timed out" })
          setStatus("ERROR")
          trackFail("OFFLINE", "Request timed out")
          unsubRef.current = null
        }, 60_000)
      }

      const safeParse = (e) => {
        try {
          return JSON.parse(e.data)
        } catch {
          stream.close()
          clearTimeout(timeout)
          setError({ kind: "SERVER", message: "Bad response from server" })
          setStatus("ERROR")
          trackFail("SERVER", "Bad response from server")
          unsubRef.current = null
          return null
        }
      }

      resetTimeout()

      const elapsed = () => `${Math.round(performance.now() - startedAt)}ms`

      stream.addEventListener("tool", (e) => {
        const data = safeParse(e)
        if (!data) return
        resetTimeout()
        markStreaming()
        console.log("[EqSolve solver] tool", { t: elapsed(), index: data.index, action: data.action })
        const call = formatToolCall(data.action, data.input, String(data.result ?? ""))
        collected.set(data.index, { index: data.index, call, explanation: null })
        setSteps((prev) => [...prev, { index: data.index, call, explanation: null }])
      })

      stream.addEventListener("step", (e) => {
        const data = safeParse(e)
        if (!data) return
        resetTimeout()
        markStreaming()
        console.log("[EqSolve solver] step", { t: elapsed(), index: data.index })
        const existing = collected.get(data.index) || { index: data.index, call: null }
        collected.set(data.index, { ...existing, explanation: data.explanation })
        setSteps((prev) => {
          if (prev.some((s) => s.index === data.index)) {
            return prev.map((s) => (s.index === data.index ? { ...s, explanation: data.explanation } : s))
          }
          return [...prev, { index: data.index, call: null, explanation: data.explanation }]
        })
      })

      stream.addEventListener("answer", (e) => {
        const data = safeParse(e)
        if (!data) return
        resetTimeout()
        markStreaming()
        console.log("[EqSolve solver] answer", { t: elapsed(), verified: data.verified, data })
        answerData = data
        setAnswer(data)
      })

      stream.addEventListener("error", (e) => {
        const data = safeParse(e)
        if (!data) return
        errored = true
        clearTimeout(timeout)
        console.warn("[EqSolve solver] server error", { t: elapsed(), message: data.message })
        setError({ kind: "SERVER", message: data.message })
        setStatus("ERROR")
        trackFail("SERVER", data.message)
        unsubRef.current = null
      })

      stream.addEventListener("done", () => {
        stream.close()
        clearTimeout(timeout)
        if (!errored) {
          console.log("[EqSolve solver] done", { t: elapsed() })
          unsubRef.current = null
          setStatus("DONE")
          const steps = [...collected.values()]
          track("solve", {
            problem: nextProblem,
            level: nextLevel,
            region,
            source,
            steps_count: steps.length,
            verified: answerData?.verified,
            problem_type: answerData?.problem_type,
            answer_symbolic: answerData?.answer?.symbolic,
            answer_latex: answerData?.answer_latex,
            steps,
            $set: { region, level: nextLevel },
          })
        }
      })

      stream.onerror = () => {
        stream.close()
        clearTimeout(timeout)
        if (!errored) {
          console.warn("[EqSolve solver] connection lost", { t: elapsed() })
          setError({ kind: "OFFLINE", message: "Connection lost" })
          setStatus("ERROR")
          trackFail("OFFLINE", "Connection lost")
        }
        unsubRef.current = null
      }

      unsubRef.current = () => {
        stream.close()
        clearTimeout(timeout)
      }
    },
    [stop]
  )

  useEffect(() => () => stop(), [stop])

  return { status, problem, level, steps, answer, error, run, stop, reset, loadCached }
}
